import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react'

import CssBaseline from '@mui/material/CssBaseline'
import { ThemeProvider } from '@mui/material/styles'

import { makeTheme, type ThemeMode } from '../theme'

// ThemeModeProvider owns the app's light/dark selection, persists it to
// localStorage, and hands MUI the right theme instance. Sidebar/TopBar
// call useThemeMode() to render the toggle icon and switch on click.
//
// The sidebar itself stays indigo in both modes — see SB_* constants
// in theme.ts. Only the page ground and cards flip.

type ThemeModeState = {
  mode: ThemeMode
  toggle: () => void
  setMode: (m: ThemeMode) => void
}

const Ctx = createContext<ThemeModeState | undefined>(undefined)

const STORAGE_KEY = 'fieldlens.theme-mode'

function readInitial(): ThemeMode {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw === 'light' || raw === 'dark') return raw
  } catch { /* localStorage disabled */ }
  return 'light'
}

export function ThemeModeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<ThemeMode>(readInitial)

  const setMode = useCallback((next: ThemeMode) => {
    setModeState(next)
    try { localStorage.setItem(STORAGE_KEY, next) } catch { /* ignore */ }
  }, [])

  const toggle = useCallback(() => {
    setMode(mode === 'light' ? 'dark' : 'light')
  }, [mode, setMode])

  const value = useMemo<ThemeModeState>(() => ({ mode, toggle, setMode }), [mode, toggle, setMode])
  const muiTheme = useMemo(() => makeTheme(mode), [mode])

  return (
    <Ctx.Provider value={value}>
      <ThemeProvider theme={muiTheme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </Ctx.Provider>
  )
}

export function useThemeMode(): ThemeModeState {
  const v = useContext(Ctx)
  if (!v) throw new Error('useThemeMode must be used inside <ThemeModeProvider>')
  return v
}
